/**
 * Sector operations — Hospital Bed States client service.
 * R39 C7.a + C7.b + C3.a (plan docs/plans/sector-operations.md §Part C, SO-P251, SO-D17, SO-P7).
 */

import {
  collection,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { OpsSubscriptionAccessOptions } from './opsRostersService'
import type {
  OpsBedState,
  SetBedStateInput,
  SetBedStateResult,
  ListBedEventsInput,
  ListBedEventsResult,
} from '../types/ops'

// ---------------------------------------------------------------------------
// 1. Subscriptions & Direct Reads
// ---------------------------------------------------------------------------

export function subscribeToBedStates(
  workspaceId: string,
  wardPlaceId: string | null | undefined,
  callback: (states: OpsBedState[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const bedStatesRef = collection(firestoreDb, 'ops_bed_states')

  if (accessScope?.isRestricted === true) {
    const callerUid = accessScope.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToBedStates'))
      return () => undefined
    }

    const qNull = query(
      bedStatesRef,
      where('workspaceId', '==', workspaceId),
      where('readAccess', '==', null),
    )

    const qUid = query(
      bedStatesRef,
      where('workspaceId', '==', workspaceId),
      where('readAccess', 'array-contains', callerUid),
    )

    let statesNull: OpsBedState[] = []
    let statesUid: OpsBedState[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsBedState>()
      for (const s of statesNull) {
        map.set(s.placeId, s)
      }
      for (const s of statesUid) {
        map.set(s.placeId, s)
      }
      const all = Array.from(map.values())
      const filtered = wardPlaceId
        ? all.filter((s) => !s.wardPlaceId || s.wardPlaceId === wardPlaceId)
        : all
      callback(filtered)
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        statesNull = snap.docs.map((d) => d.data() as OpsBedState)
        mergeAndEmit()
      },
      (err) => onError?.(err),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        statesUid = snap.docs.map((d) => d.data() as OpsBedState)
        mergeAndEmit()
      },
      (err) => onError?.(err),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = query(
    bedStatesRef,
    where('workspaceId', '==', workspaceId),
  )

  return onSnapshot(
    q,
    (snap) => {
      const all = snap.docs.map((d) => d.data() as OpsBedState)
      const filtered = wardPlaceId
        ? all.filter((s) => !s.wardPlaceId || s.wardPlaceId === wardPlaceId)
        : all
      callback(filtered)
    },
    (err) => onError?.(err),
  )
}

export function subscribeToBedStatesForBeds(
  workspaceId: string,
  bedPlaceIds: string[],
  callback: (states: OpsBedState[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId || bedPlaceIds.length === 0) {
    callback([])
    return () => undefined
  }
  const bedSet = new Set(bedPlaceIds)
  return subscribeToBedStates(
    workspaceId,
    null,
    (states) => {
      callback(states.filter((s) => bedSet.has(s.placeId)))
    },
    onError,
    accessScope,
  )
}

// ---------------------------------------------------------------------------
// 2. Callables
// ---------------------------------------------------------------------------

export async function setBedState(input: SetBedStateInput): Promise<SetBedStateResult> {
  return callable<SetBedStateInput, SetBedStateResult>('setBedState', input)
}

export async function listBedEvents(input: ListBedEventsInput): Promise<ListBedEventsResult> {
  return callable<ListBedEventsInput, ListBedEventsResult>('listBedEvents', input)
}
